import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BriefTheme, DailyBrief } from '@mg/shared';
import {
  ExternalLink,
  FileText,
  MessageSquare,
  Rss,
  Settings,
  Sparkles,
  Tags,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import { api, ApiRequestError } from '../lib/api';
import { useTenantKey } from '../lib/useTenantKey';
import { useTenant } from '../auth/TenantContext';
import { formatDateLong } from '../lib/display';
import { PageTitle } from '../components/shell/PageHeader';
import { Card } from '../components/ui/Card';
import { Select } from '../components/ui/Field';
import { Button } from '../components/ui/Button';
import { EmptyState, ErrorState, LoadingState } from '../components/ui/States';
import { ThemeSourcesModal } from '../features/briefs/ThemeSourcesModal';
import { themeIcon } from '../features/briefs/themeIcon';
import { ThemeThumbnail, themeImageSources } from '../features/briefs/ThemeThumbnail';

/** Local YYYY-MM-DD for "today" so the digest defaults to the current date. */
function todayIso(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Social Digest — AI-curated content themes from a tenant's sources for a given
 * day. Defaults to today; falls back to a "generate digest" empty state.
 */
export function DailyBriefPage() {
  const tenantKey = useTenantKey();
  const { activeTenant } = useTenant();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const today = useMemo(() => todayIso(), []);

  /** Which theme+action triggered a navigation, so we can show a per-button spinner. */
  const [writingThemeId, setWritingThemeId] = useState<string | null>(null);
  /** Desired number of theme cards when (re)generating the digest. */
  const [maxThemes, setMaxThemes] = useState(5);
  /** The theme whose contributing sources are being viewed (null = closed). */
  const [viewingTheme, setViewingTheme] = useState<BriefTheme | null>(null);

  // Fetch the LATEST brief (no date) rather than today's local date — briefs are
  // keyed to UTC midnight, so requesting the browser-local date misses the brief
  // whenever the local date differs from the UTC date. Treat 404 (no brief yet)
  // as an empty state rather than an error.
  const briefQuery = useQuery({
    queryKey: ['brief', tenantKey],
    queryFn: async ({ signal }) => {
      try {
        return await api.getBrief(undefined, signal);
      } catch (err) {
        if (err instanceof ApiRequestError && err.status === 404) return null;
        throw err;
      }
    },
    enabled: tenantKey !== '',
  });

  const brief: DailyBrief | undefined = briefQuery.data ?? undefined;
  const briefDate = brief?.briefDate ?? today;

  // The user's Trend Topics — one of the two inputs (alongside Brand Voice) the
  // "% match" is scored against. When the digest has themes but the user hasn't
  // added any topics yet, nudge them toward /sources?tab=topics to sharpen matches.
  const topicsQuery = useQuery({
    queryKey: ['trend-topics', tenantKey],
    queryFn: ({ signal }) => api.listTrendTopics(signal),
    enabled: tenantKey !== '',
  });
  const showTopicsHint =
    !!brief && brief.themes.length > 0 && topicsQuery.data?.length === 0;

  const generate = useMutation({
    mutationFn: () => api.generateBrief(undefined, maxThemes),
    onSuccess: (result) => {
      queryClient.setQueryData(['brief', tenantKey], result);
    },
  });

  /**
   * "Write Post" — spin up a Campaign (with its first Post) from the theme and
   * open the composer on that first post (fall back to the campaign detail if none).
   */
  const writeBlogPost = useMutation({
    mutationFn: (theme: BriefTheme) => {
      if (!brief) throw new Error('No digest loaded');
      return api.themeToStrategist(brief.id, theme.id);
    },
    onSuccess: (campaign) => {
      void queryClient.invalidateQueries({ queryKey: ['campaigns', tenantKey] });
      void queryClient.invalidateQueries({ queryKey: ['posts', tenantKey] });
      const firstPost = campaign.posts?.[0];
      if (firstPost) {
        const next = new URLSearchParams(params);
        next.set('compose', firstPost.id);
        setParams(next, { replace: true });
      } else {
        navigate(`/campaigns/${campaign.id}`);
      }
    },
    onSettled: () => setWritingThemeId(null),
  });


  const onWriteBlogPost = (theme: BriefTheme) => {
    setWritingThemeId(theme.id);
    writeBlogPost.mutate(theme);
  };
  /**
   * "Write Post with Strategist" — go to the Strategist NOW and let it do the seeding.
   *
   * This used to create the chat, wait for the model's whole first answer, and only then navigate,
   * so the button sat spinning here for as long as the strategist took to think. The page owns that
   * wait far better: it can show the prompt that was sent on your behalf while the answer is still
   * coming. Nothing is created until the Strategist page asks for it, so a click that never lands
   * leaves no empty conversation behind.
   */
  const onSendToStrategist = (theme: BriefTheme) => {
    if (!brief) return;
    navigate('/strategist', { state: { seedTheme: { briefId: brief.id, themeId: theme.id } } });
  };

  const header = (
    <div className="mb-6">
      <div className="flex items-start justify-between gap-4">
        <PageTitle
          title="Social Trends"
          icon={Sparkles}
          description={`${formatDateLong(briefDate)}${activeTenant ? ` · ${activeTenant.name}` : ''}`}
        />
        <div className="flex items-center gap-2">
          {/* Not a <label> wrapper: the Select's own control is a button preceded by a hidden
              native select, so a wrapping label would bind to the hidden one and clicking the
              text would do nothing. The aria-label carries the naming instead. */}
          <div className="flex items-center gap-1.5 text-sm text-ink-muted">
            <Settings className="h-4 w-4" aria-hidden="true" />
            Cards
            <Select
              value={maxThemes}
              onChange={(e) => setMaxThemes(Number(e.target.value))}
              className="w-20"
              aria-label="Number of theme cards to generate"
            >
              {[3, 4, 5, 6, 7, 8].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          </div>
          <Button
            variant="secondary"
            size="sm"
            loading={generate.isPending}
            onClick={() => generate.mutate()}
          >
            {!generate.isPending && <Sparkles className="h-4 w-4" aria-hidden="true" />}
            {brief ? 'Regenerate' : 'Generate'}
          </Button>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-muted">
          AI-curated content themes from your sources · Each theme&apos;s{' '}
          <span className="font-semibold text-ink-strong">% match</span> is scored against your Trend
          Topics + Brand Voice.
        </p>
        {/* Global links to tune what drives these themes — one set for the page (was per-card). */}
        <div className="flex shrink-0 items-center gap-2">
          <SourceLink to="/sources" icon={Rss}>
            Digest Sources
          </SourceLink>
          <SourceLink to="/sources?tab=topics" icon={Tags}>
            Trend Topics
          </SourceLink>
        </div>
      </div>
      {generate.isError && (
        <p className="mt-2 text-sm text-red-600 dark:text-error-dark">
          {generate.error instanceof ApiRequestError
            ? generate.error.message
            : 'Could not generate the digest.'}
        </p>
      )}
    </div>
  );

  return (
    <>
      {header}

      {showTopicsHint && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line-soft bg-tint px-4 py-2.5 text-sm text-tint-fg">
          <span className="inline-flex items-center gap-2">
            <Tags className="h-4 w-4" aria-hidden="true" />
            Add Trend Topics to sharpen these matches.
          </span>
          <Link
            to="/sources?tab=topics"
            className="focus-ring rounded font-medium text-link hover:text-action-hover hover:underline"
          >
            Manage topics →
          </Link>
        </div>
      )}

      {briefQuery.isLoading ? (
        <LoadingState label="Loading digest…" />
      ) : briefQuery.isError ? (
        <ErrorState
          message={
            briefQuery.error instanceof ApiRequestError
              ? briefQuery.error.message
              : 'Could not load the digest.'
          }
          onRetry={() => void briefQuery.refetch()}
        />
      ) : !brief || brief.themes.length === 0 ? (
        <Card>
          <EmptyState
            title="No digest yet"
            description="Generate today's digest to cluster your latest source items into content themes."
            action={
              <Button loading={generate.isPending} onClick={() => generate.mutate()}>
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                Generate digest
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="space-y-5">
          {brief.themes.map((theme) => (
            <ThemeCard
              key={theme.id}
              theme={theme}
              writing={writingThemeId === theme.id && writeBlogPost.isPending}
              onWrite={() => onWriteBlogPost(theme)}
              onSend={() => onSendToStrategist(theme)}
              onViewSources={() => setViewingTheme(theme)}
            />
          ))}
        </div>
      )}

      <ThemeSourcesModal
        briefId={brief?.id ?? null}
        theme={viewingTheme}
        onClose={() => setViewingTheme(null)}
      />
    </>
  );
}

function ThemeCard({
  theme,
  writing,
  onWrite,
  onSend,
  onViewSources,
}: {
  theme: BriefTheme;
  writing: boolean;
  onWrite: () => void;
  onSend: () => void;
  onViewSources: () => void;
}) {
  return (
    <Card className="p-5">
      <div className="flex flex-wrap gap-4">
        <ThemeThumbnail
          key={theme.id}
          sources={themeImageSources(theme)}
          alt={theme.title}
          fallback={themeIcon(theme)}
        />

        <div className="min-w-[16rem] flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-ink">{theme.title}</h2>
            <span
              className="inline-flex items-center rounded-full bg-tint px-2 py-0.5 text-xs font-medium text-tint-fg"
              title="% match = how well this theme fits your Trend Topics + Brand Voice"
            >
              {theme.matchScore}% match
            </span>
          </div>

          {(theme.matchedTopics?.length ?? 0) > 0 && (
            <p className="mt-1 break-words text-xs text-ink-muted">
              matches: {theme.matchedTopics.join(' · ')}
            </p>
          )}

          <p className="mt-1.5 text-sm text-ink-label">{theme.summary}</p>

          {theme.suggestedAngles.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-semibold text-ink">Suggested Angles</p>
              <ul className="mt-2 space-y-1.5">
                {theme.suggestedAngles.map((angle, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-link">
                    <span
                      className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-action"
                      aria-hidden="true"
                    />
                    <span>{angle}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-line-soft pt-4">
        <div className="flex items-center gap-3 text-sm text-ink-muted">
          <span className="inline-flex items-center gap-1">
            <TrendingUp className="h-4 w-4" aria-hidden="true" />
            {theme.sourcesCount} source{theme.sourcesCount === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            onClick={onViewSources}
            disabled={theme.sourcesCount === 0}
            className="focus-ring inline-flex items-center gap-1 rounded text-ink-muted hover:text-ink-strong disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
            View sources
          </button>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" loading={writing} onClick={onWrite}>
            {!writing && <FileText className="h-4 w-4" aria-hidden="true" />}
            Write Post
          </Button>
          <Button size="sm" onClick={onSend}>
            <MessageSquare className="h-4 w-4" aria-hidden="true" />
            Write Post with Strategist
          </Button>
        </div>
      </div>
    </Card>
  );
}

/** A right-rail link styled like a secondary button (routes to a Digest Sources tab). */
function SourceLink({ to, icon: Icon, children }: { to: string; icon: LucideIcon; children: string }) {
  return (
    <Link
      to={to}
      className="focus-ring inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-line-input bg-card px-3 py-1.5 text-sm font-medium text-ink-strong transition-colors hover:bg-hover"
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {children}
    </Link>
  );
}

